import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { quikqAuthService } from '../services/quikqAuth';
import { useNavigate } from 'react-router-dom';

const TwoFactorAuth: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    twoFactorCode: '',
    rememberDevice: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      rememberDevice: event.target.checked
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await quikqAuthService.verifyTwoFactor({
        twoFactorCode: formData.twoFactorCode,
        rememberDevice: formData.rememberDevice
      });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" gutterBottom>
            Enter the security code sent to your email
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="twoFactorCode"
              label="Security Code"
              name="twoFactorCode"
              autoComplete="off"
              value={formData.twoFactorCode}
              onChange={handleInputChange('twoFactorCode')}
              placeholder="Enter 6-digit code"
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.rememberDevice}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="Remember this device for 12 hours"
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Didn't receive the code? Check your email or contact support.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TwoFactorAuth; 